<template>
  <div v-if="ready" class="box">
    <p v-if="active">
      <a
        class="title is-5 is-unselectable"
        @click="
          active = !active;
          $emit('changed', null);
        "
      >
        <span v-show="isValid">
          <span class="icon has-text-success">
            <i class="mdi mdi-check has-text-success"></i>
          </span>
        </span>
        Schedule <span v-if="!isValid">(not active)</span>
        <span class="icon">
          <i class="mdi mdi-chevron-up"></i>
        </span>
      </a>
    </p>

    <a
      class="title is-5 is-unselectable"
      v-if="!active"
      @click="active = !active"
    >
      Schedule (off)
      <span class="icon">
        <i class="mdi mdi-chevron-down"></i>
      </span>
    </a>

    <div class="columns is-multiline" v-show="active">
      <div class="clickable column is-12">
        <TipsNotification
          path="groupPackages"
          xKey="dismissedTips"
          property="schedule"
        >
          <p>
            The command will take place inside the date span, time span and on
            the selected day(s).
          </p>

          <p>
            Take the default settings as an example. The command will execute,
            when possible, between today (start date) and 7 days ahead (end
            date), between 6 PM (start time) and 6 AM (end time) at the selected
            days of the week.
          </p>

          <p>
            The idea in this example is to set the <i>time span</i> outside
            normal working hours and the <i>date span</i> within 7 days. Though,
            at least one week day must be selected. If Monday was selected, the
            command would only start on a Monday.
          </p>
        </TipsNotification>
      </div>

      <template v-if="showDatePicker">
        <div class="column is-6">
          Start date
          <DatePicker
            v-if="active"
            type="date"
            :startDate="newSchedule.startDate"
            @selected="setScheduleValue('startDate', $event)"
          ></DatePicker>
        </div>

        <div class="column is-6">
          Start time
          <DatePicker
            v-if="active"
            type="time"
            :startTime="newSchedule.startTime"
            @selected="setScheduleValue('startTime', $event)"
          ></DatePicker>
        </div>

        <div class="column is-6">
          End date
          <DatePicker
            v-if="active"
            type="date"
            :startDate="defaultEndDate"
            @selected="setScheduleValue('endDate', $event)"
          ></DatePicker>
        </div>

        <div class="column is-6">
          End time
          <DatePicker
            v-if="active"
            type="time"
            :startTime="defaultEndTime"
            @selected="setScheduleValue('endTime', $event)"
          ></DatePicker>
        </div>
      </template>

      <div class="column is-12">
        <div class="columns is-multiline extrapadding">
          <CheckboxText
            class="margin-right-10"
            :checked="hasParam(newSchedule.repeatWeekDays, '1')"
            @click="addWeekDayParam(newSchedule.repeatWeekDays, '1')"
            data-tooltip="Monday"
            >Mon</CheckboxText
          >
          <CheckboxText
            class="margin-right-10"
            :checked="hasParam(newSchedule.repeatWeekDays, '2')"
            @click="addWeekDayParam(newSchedule.repeatWeekDays, '2')"
            data-tooltip="Tuesday"
            >Tue</CheckboxText
          >
          <CheckboxText
            class="margin-right-10"
            :checked="hasParam(newSchedule.repeatWeekDays, '3')"
            @click="addWeekDayParam(newSchedule.repeatWeekDays, '3')"
            data-tooltip="Wednesday"
            >Wed</CheckboxText
          >
          <CheckboxText
            class="margin-right-10"
            :checked="hasParam(newSchedule.repeatWeekDays, '4')"
            @click="addWeekDayParam(newSchedule.repeatWeekDays, '4')"
            data-tooltip="Thursday"
            >Thu</CheckboxText
          >
          <CheckboxText
            class="margin-right-10"
            :checked="hasParam(newSchedule.repeatWeekDays, '5')"
            @click="addWeekDayParam(newSchedule.repeatWeekDays, '5')"
            data-tooltip="Friday"
            >Friday</CheckboxText
          >
          <CheckboxText
            class="margin-right-10"
            :checked="hasParam(newSchedule.repeatWeekDays, '6')"
            @click="addWeekDayParam(newSchedule.repeatWeekDays, '6')"
            data-tooltip="Saturday"
          >
            <span class="has-text-danger">Sat</span>
          </CheckboxText>
          <CheckboxText
            class="margin-right-10"
            :checked="hasParam(newSchedule.repeatWeekDays, '7')"
            @click="addWeekDayParam(newSchedule.repeatWeekDays, '7')"
            data-tooltip="Sunday"
          >
            <span class="has-text-danger">Sun</span>
          </CheckboxText>

          <div>
            <CheckboxText
              :checked="newSchedule.type === 1"
              @click="toggleType()"
              data-tooltip="Auto upgrade within the schedule"
              >Auto upgrade</CheckboxText
            >
          </div>
        </div>

        <div
          class="right has-tooltip-left"
          data-tooltip="Quick settings for auto upgrade"
        >
          <span @click="checkAllDays()" class="icon clickable has-text-primary">
            <i class="mdi mdi-auto-fix"></i>
          </span>
        </div>

        <IconText
          v-if="
            !newSchedule.repeatWeekDays || newSchedule.repeatWeekDays === ''
          "
          icon="mdi-alert has-text-warning"
          ><span class="has-text-danger"
            >Select at least one day.</span
          ></IconText
        >
      </div>
    </div>
  </div>
</template>

<script>
import { hasParam } from "@/utils/mdc";
import { addTempParam } from "@/utils/packages";
import { isScheduleValid, objectifySchedule } from "@/utils/packageDate";

export default {
  components: {
    CheckboxText: () => import("@/components/singles/CheckboxText.vue"),
    IconText: () => import("@/components/singles/IconText.vue"),
    DatePicker: () => import("@/components/singles/DatePicker.vue"),
    TipsNotification: () => import("@/components/main/TipsNotification.vue"),
  },
  props: { schedule: Object },

  data() {
    return {
      forceShowTip: false,
      active: false,
      defaultEndDate: new Date(),
      defaultEndTime: new Date(),
      newSchedule: null,
      ready: false,
      showDatePicker: true,
    };
  },
  computed: {
    isValid() {
      return isScheduleValid(this.newSchedule);
    },
    hasParam() {
      return (str, param) => {
        return hasParam(str, param);
      };
    },
  },
  created() {
    this.$set(this, "newSchedule", objectifySchedule({ ...this.schedule }));

    this.active = isScheduleValid(this.newSchedule);

    this.setDefaultTimeAndDate();

    this.ready = true;

    this.validateSchedule();
  },
  methods: {
    setDefaultTimeAndDate() {
      this.defaultEndDate =
        this.newSchedule.endDate ||
        new Date(
          this.defaultEndDate.setDate(this.defaultEndDate.getDate() + 7)
        );

      this.defaultEndTime =
        this.newSchedule.endTime ||
        new Date(new Date(this.defaultEndTime.setHours(6)).setMinutes(0));
    },
    validateSchedule() {
      const isValid = isScheduleValid(this.newSchedule);
      if (isValid) this.$emit("changed", { ...this.newSchedule });
      else this.$emit("changed", null);
    },
    checkAllDays() {
      this.setDefaultTimeAndDate();
      this.setScheduleValue("repeatWeekDays", "1|2|3|4|5|6|7");
      this.setScheduleValue("type", 1);

      const d = new Date();
      d.setFullYear(d.getFullYear() + 50);
      this.defaultEndDate = d;

      this.showDatePicker = false;
      this.$nextTick(() => {
        this.showDatePicker = true;
      });
    },

    addWeekDayParam(str, param) {
      const newParam = addTempParam(str, param);
      this.setScheduleValue("repeatWeekDays", newParam);
    },

    toggleType() {
      if (this.newSchedule.type === 1) this.setScheduleValue("type", 0);
      else this.setScheduleValue("type", 1);
    },
    setScheduleValue(key, value) {
      this.$set(this.newSchedule, key, value);
      this.validateSchedule();
    },
  },
};
</script>
